import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { 
  ClipboardCheck, Calendar, Loader2, Plus, TrendingUp, TrendingDown,
  Minus, AlertCircle, CheckCircle, Zap, Target, Dumbbell,
  ChevronRight, Eye
} from 'lucide-react';
import { toast } from 'sonner';
import PlayerProfiling from './PlayerProfiling';
import MesocyclePlanViewer from './MesocyclePlanViewer';
import { API } from '../config/api';

export default function ProfilingTab({ childId, childName, user }) {
  const [loading, setLoading] = useState(true);
  const [latestProfile, setLatestProfile] = useState(null);
  const [activePlan, setActivePlan] = useState(null);
  const [profileHistory, setProfileHistory] = useState([]);
  const [showProfiling, setShowProfiling] = useState(false);
  const [showPlanViewer, setShowPlanViewer] = useState(false);

  useEffect(() => {
    fetchData();
  }, [childId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Fetch latest profile
      const profileRes = await axios.get(`${API}/profiling/athlete/${childId}/latest-profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLatestProfile(profileRes.data.profile);
      
      // Fetch profile history
      const historyRes = await axios.get(`${API}/profiling/athlete/${childId}/profiles?limit=5`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfileHistory(historyRes.data.profiles || []);
      
      // Fetch active plan
      const plansRes = await axios.get(`${API}/mesocycle/athlete/${childId}/plans?status=active`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (plansRes.data.plans?.length > 0) {
        setActivePlan(plansRes.data.plans[0]);
      }
    } catch (err) {
      console.error('Failed to fetch profiling data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleProfileSaved = () => {
    fetchData();
    // Automatically show plan viewer after profile is saved
    setTimeout(() => setShowPlanViewer(true), 500);
  };

  const getRatingColor = (rating) => {
    const colors = {
      1: 'text-red-400',
      2: 'text-orange-400',
      3: 'text-yellow-400',
      4: 'text-green-400',
      5: 'text-emerald-400'
    };
    return colors[Math.round(rating)] || 'text-zinc-400';
  };

  const getRatingBgColor = (rating) => {
    const colors = {
      1: 'bg-red-500/20',
      2: 'bg-orange-500/20',
      3: 'bg-yellow-500/20',
      4: 'bg-green-500/20',
      5: 'bg-emerald-500/20'
    };
    return colors[Math.round(rating)] || 'bg-zinc-700';
  };

  const isCoachOrStaff = user?.role === 'coach' || user?.role === 'epp_staff' || user?.role === 'admin';

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-[#02b5ff]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Actions */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white">Player Profiling</h2>
          <p className="text-sm text-zinc-400">Assessment & Training Plan Management</p>
        </div>
        {isCoachOrStaff && (
          <div className="flex gap-3">
            <Button
              onClick={() => setShowProfiling(true)}
              className="bg-gradient-to-r from-[#02b5ff] to-[#0090cc] text-white"
              data-testid="new-assessment-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              New Assessment
            </Button>
            <Button
              onClick={() => setShowPlanViewer(true)}
              variant="outline"
              className="border-[#ff025f] text-[#ff025f] hover:bg-[#ff025f]/10"
              data-testid="view-plan-btn"
            >
              <Calendar className="h-4 w-4 mr-2" />
              {activePlan ? 'View Plan' : 'Generate Plan'}
            </Button>
          </div>
        )}
      </div>

      {/* Latest Profile Summary */}
      {latestProfile ? (
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl overflow-hidden">
          <div className="p-6 border-b border-zinc-800">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-[#02b5ff]/20 to-[#ff025f]/20 rounded-xl flex items-center justify-center">
                  <ClipboardCheck className="h-6 w-6 text-[#02b5ff]" />
                </div>
                <div>
                  <h3 className="font-semibold text-white">Latest Assessment</h3>
                  <p className="text-sm text-zinc-400">{latestProfile.assessment_date}</p>
                </div>
              </div>
              <div className="text-right">
                <p className={`text-3xl font-bold ${getRatingColor(latestProfile.overall_score)}`}>
                  {latestProfile.overall_score}/5
                </p>
                <p className="text-xs text-zinc-500">Overall Score</p>
              </div>
            </div>

            {/* Category Scores */}
            <div className="grid grid-cols-3 gap-4">
              {[
                { key: 'movement', label: 'Movement', icon: <Zap className="h-5 w-5" />, color: 'blue' },
                { key: 'strength', label: 'Strength', icon: <Dumbbell className="h-5 w-5" />, color: 'orange' },
                { key: 'performance', label: 'Performance', icon: <Target className="h-5 w-5" />, color: 'green' }
              ].map(cat => {
                const score = latestProfile.category_averages?.[cat.key];
                return (
                  <div 
                    key={cat.key}
                    className={`p-4 rounded-xl ${getRatingBgColor(score)} border border-zinc-700/50`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`${cat.color === 'blue' ? 'text-blue-400' : cat.color === 'orange' ? 'text-orange-400' : 'text-green-400'}`}>
                        {cat.icon}
                      </span>
                      <span className="text-sm text-zinc-300">{cat.label}</span>
                    </div>
                    <p className={`text-2xl font-bold ${getRatingColor(score)}`}>
                      {score ? score.toFixed(1) : 'N/A'}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Strengths & Weaknesses */}
          <div className="grid md:grid-cols-2 gap-0 divide-x divide-zinc-800">
            {/* Weaknesses */}
            <div className="p-6">
              <h4 className="font-medium text-red-400 mb-3 flex items-center gap-2">
                <AlertCircle className="h-4 w-4" />
                Areas to Improve ({latestProfile.identified_weaknesses?.length || 0})
              </h4>
              {latestProfile.identified_weaknesses?.length > 0 ? (
                <div className="space-y-2">
                  {latestProfile.identified_weaknesses.slice(0, 5).map((w, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-red-500/10 rounded-lg">
                      <span className="text-sm text-white">{w.name}</span>
                      <span className="text-xs text-red-400">{w.rating}/5</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-zinc-500">No significant weaknesses identified</p>
              )}
            </div>

            {/* Strengths */}
            <div className="p-6">
              <h4 className="font-medium text-green-400 mb-3 flex items-center gap-2">
                <CheckCircle className="h-4 w-4" />
                Strengths ({latestProfile.identified_strengths?.length || 0})
              </h4>
              {latestProfile.identified_strengths?.length > 0 ? (
                <div className="space-y-2">
                  {latestProfile.identified_strengths.slice(0, 5).map((s, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-green-500/10 rounded-lg">
                      <span className="text-sm text-white">{s.name}</span>
                      <span className="text-xs text-green-400">{s.rating}/5</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-zinc-500">Complete more assessments to identify strengths</p>
              )}
            </div>
          </div>

          {/* Notes */}
          {latestProfile.overall_notes && (
            <div className="p-6 border-t border-zinc-800 bg-zinc-900/30">
              <p className="text-sm text-zinc-400">
                <span className="text-zinc-300 font-medium">Notes: </span>
                {latestProfile.overall_notes}
              </p>
            </div>
          )}
        </div>
      ) : (
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-12 text-center">
          <div className="w-16 h-16 bg-zinc-800 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <ClipboardCheck className="h-8 w-8 text-zinc-500" />
          </div>
          <h3 className="text-lg font-semibold text-white mb-2">No Assessment Yet</h3>
          <p className="text-zinc-400 mb-6 max-w-md mx-auto">
            Complete a profiling assessment to identify strengths, weaknesses, and generate a personalized training plan.
          </p>
          {isCoachOrStaff && (
            <Button
              onClick={() => setShowProfiling(true)}
              className="bg-gradient-to-r from-[#02b5ff] to-[#ff025f] text-white"
            >
              <Plus className="h-4 w-4 mr-2" />
              Start First Assessment
            </Button>
          )}
        </div>
      )}

      {/* Active Training Plan */}
      {activePlan && (
        <div className="bg-gradient-to-r from-[#02b5ff]/10 to-[#ff025f]/10 border border-[#02b5ff]/30 rounded-2xl p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-gradient-to-br from-[#02b5ff] to-[#ff025f] rounded-xl flex items-center justify-center">
                <Calendar className="h-6 w-6 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-white">Active 4-Week Mesocycle Plan</h3>
                <p className="text-sm text-zinc-400">
                  {activePlan.start_date} to {activePlan.end_date} • {activePlan.sessions_per_week} sessions/week
                </p>
              </div>
            </div>
            <Button
              onClick={() => setShowPlanViewer(true)}
              className="bg-white/10 text-white hover:bg-white/20"
            >
              <Eye className="h-4 w-4 mr-2" />
              View Plan
            </Button>
          </div>
          
          {activePlan.focus_areas?.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2">
              <span className="text-xs text-zinc-400">Focus:</span>
              {activePlan.focus_areas.map((area, idx) => (
                <span key={idx} className="px-2 py-1 bg-[#ff025f]/20 text-[#ff025f] rounded text-xs">
                  {area}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Assessment History */}
      {profileHistory.length > 1 && (
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-6">
          <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-[#02b5ff]" />
            Assessment History
          </h3>
          <div className="space-y-3">
            {profileHistory.map((profile, idx) => (
              <div 
                key={profile.id}
                className={`flex items-center justify-between p-3 rounded-lg ${
                  idx === 0 ? 'bg-[#02b5ff]/10 border border-[#02b5ff]/30' : 'bg-zinc-800/50'
                }`}
              >
                <div className="flex items-center gap-3">
                  <span className="text-sm text-zinc-400">{profile.assessment_date}</span>
                  {idx === 0 && <span className="px-2 py-0.5 bg-[#02b5ff]/20 text-[#02b5ff] rounded text-xs">Latest</span>}
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <span className={`font-bold ${getRatingColor(profile.overall_score)}`}>
                      {profile.overall_score}/5
                    </span>
                  </div>
                  {idx > 0 && profileHistory[idx - 1] && (
                    <span className={`flex items-center gap-1 text-xs ${
                      profile.overall_score < profileHistory[idx - 1].overall_score 
                        ? 'text-green-400' 
                        : profile.overall_score > profileHistory[idx - 1].overall_score 
                        ? 'text-red-400' 
                        : 'text-zinc-400'
                    }`}>
                      {profile.overall_score < profileHistory[idx - 1].overall_score ? (
                        <TrendingUp className="h-3 w-3" />
                      ) : profile.overall_score > profileHistory[idx - 1].overall_score ? (
                        <TrendingDown className="h-3 w-3" />
                      ) : (
                        <Minus className="h-3 w-3" />
                      )}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Modals */}
      {showProfiling && (
        <PlayerProfiling
          childId={childId}
          childName={childName}
          onClose={() => setShowProfiling(false)}
          onProfileSaved={handleProfileSaved}
        />
      )}

      {showPlanViewer && (
        <MesocyclePlanViewer
          childId={childId}
          childName={childName}
          planId={activePlan?.id}
          onClose={() => setShowPlanViewer(false)}
          onPlanGenerated={fetchData}
        />
      )}
    </div>
  );
}
